import AreYouSure from '../core/AreYouSure';
import Errored from '../core/Errored';
import LinkIcon from '@material-ui/icons/Link';
import NearMeIcon from '@material-ui/icons/NearMe';
import LabelIcon from '@material-ui/icons/Label';
import WavesIcon from '@material-ui/icons/Waves';
import BasicComponentChip from '../application-core/BasicComponentChip';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import BasicLangChip from '../core/BasicLangChip';
import { APPLICATION_PATHS, EDITING_OPTIONS } from './constants';
import ExternalResourceLink from '../core/ExternalResourceLink';
import { globalStyles as styles } from '../core/style/gstheme';
import withGS from '../core/withGShoc';
import GS from '../grafsync/grafsync';
import ReifyButton from '../core/ReifyButton';
import { getNewTabLink, makeCancelable, isInContextComponent, domainOrRange, getProjectFromGraph, computeProjectIcon } from '../core/utils/utils';
import { STATUS_REJECTED } from '../core/utils/constants';

const REJECTED = STATUS_REJECTED;

// Note important that visibility comees before isDefinedBy
const MODE = {
  Concept: {
    inComponent: "inScheme",
    applicationPath: APPLICATION_PATHS.TAXONOMY_MANAGER,
    componentName: "taxonomy",
    resourceName: "concept",
    label: "prefLabel",
    view: "concept",
    allowFloating: true
  },
  Label: {
    inComponent: "inScheme",
    applicationPath: APPLICATION_PATHS.TAXONOMY_MANAGER,
    componentName: "taxonomy",
    resourceName: "label",
    label: "literalForm",
    additionalAvatar: <LabelIcon data-test="xl-label" fontSize="small" />,
    view: "label",
    allowFloating: true
  },
  Resource: {
    inComponent: "inDataset",
    applicationPath: APPLICATION_PATHS.DATA_MANAGER,
    componentName: "dataset",
    resourceName: "resource",
    label: "label",
    allowFloating: true
  },
  OwlClass: {
    inComponent: ["visibility", "isDefinedBy"],
    applicationPath: APPLICATION_PATHS.ONTOLOGY_MANAGER,
    componentName: "ontology",
    resourceName: "class",
    view: "class",
    label: "label"
  },
  ObjectProperty: {
    inComponent: ["visibility", "isDefinedBy",],
    applicationPath: APPLICATION_PATHS.ONTOLOGY_MANAGER,
    componentName: "ontology",
    resourceName: "objectProperty",
    view: "objectProperty",
    label: "label"
  },
  DatatypeProperty: {
    inComponent: ["visibility", "isDefinedBy"],
    applicationPath: APPLICATION_PATHS.ONTOLOGY_MANAGER,
    componentName: "ontology",
    resourceName: "datatypeProperty",
    view: "datatypeProperty",
    label: "label"
  },
  AnnotationProperty: {
    inComponent: ["visibility", "isDefinedBy"],
    applicationPath: APPLICATION_PATHS.ONTOLOGY_MANAGER,
    componentName: "ontology",
    resourceName: "annotationProperty",
    view: "annotationProperty",
    label: "label"
  },
  NamedIndividual: {
    inComponent: ["visibility", "isDefinedBy"],
    applicationPath: APPLICATION_PATHS.ONTOLOGY_MANAGER,
    componentName: "ontology",
    resourceName: "namedIndividual",
    view: "namedIndividual",
    label: "label"
  },
  RdfsDatatype: {
    inComponent: ["visibility", "isDefinedBy"],
    applicationPath: APPLICATION_PATHS.ONTOLOGY_MANAGER,
    componentName: "ontology",
    resourceName: "datatype",
    view: "datatype",
    label: "label"
  },
  ConceptScheme: {
    isComponent: true,
    applicationPath: APPLICATION_PATHS.TAXONOMY_MANAGER,
    componentName: "taxonomy",
    label: "title"
  },
  Collection: {
    isComponent: true,
    applicationPath: APPLICATION_PATHS.TAXONOMY_MANAGER,
    componentName: "collection",
    label: "title"
  },
  OrderedCollection: {
    isComponent: true,
    applicationPath: APPLICATION_PATHS.TAXONOMY_MANAGER,
    componentName: "collection",
    label: "title"
  },
  Dataset: {
    isComponent: true,
    applicationPath: APPLICATION_PATHS.DATA_MANAGER,
    componentName: "dataset",
    label: "title"
  },
  Ontology: {
    isComponent: true,
    applicationPath: APPLICATION_PATHS.ONTOLOGY_MANAGER,
    componentName: "ontology",
    label: "title"
  }
};

const MODE_KEYS = Object.keys(MODE);

class CommonIriDisplay extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      areYouSure: false,
      disabled: false,
      isErrored: undefined
    };
    this.cancellable = null;
  }

  doCheck(payload) {
    this.cancellable = makeCancelable(GS.checkForInbound(payload));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  loadData(iri) {
    this.cancellable = makeCancelable(GS.loadData([iri], {}, this.props.graph, true));
    return this.cancellable;
  }

  getProjectComponents = async () => {
    const { projectIRI, graph } = this.props;
    await this.loadData(projectIRI).promise;
    if (this.cancellable && this.cancellable.isCancelled()) return;
    if (GS.isLoaded(projectIRI, graph)) {
      const projectData = GS.data(projectIRI, graph, false);
      if (projectData && "hasComponent" in projectData) {
        const components = projectData.hasComponent;
        return components;
      }
    }
    return undefined;
  };

  onSure = () => this.setState({ areYouSure: false }, this.completeDeletion);

  onNotSure = () => this.setState({ areYouSure: false });

  completeDeletion = () => {
    const graph = this.props.sourceGraph || this.props.graph;
    // If a delete function supplied call that otherwise just delete using source as the basis of the relationship
    if (this.props.onDeleteIRI) {
      this.props.onDeleteIRI(this.props.id);
    }
    else {
      // We do this directly to ensure direction of link delete is correct 
      GS.deleteProperty(this.props.source, graph, this.props.property, this.props.id, null, null, this.props.propertyGraph);
    }
  };

  handleDeleteLink = async () => {
    const onDelete = this.props.checkIfUsedOnDelete;
    const graph = this.props.sourceGraph || this.props.graph;
    // We do this directly to ensure direction of link delete is correct 
    if (onDelete) {
      try {
        // Disable the chip as this check may take a little time and we don't want double clicking
        this.setState({ disabled: true }, async () => {
          try {
            const isUsed = await this.doCheck({
              graph,
              value: onDelete.value,
              predicate: onDelete.predicate
            }).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            if (isUsed) {
              this.setState({ areYouSure: true, disabled: false });
            }
            else {
              this.completeDeletion();
            }
          }
          catch (e) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({ isErrored: e });
          }
        });
        return;
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ isErrored: e });
        return;
      }
    }
    this.completeDeletion();
  };

  // We allow a click function or a link
  handleClick = async (event) => {
    try {
      const ctrlKey = event.ctrlKey;

      if (this.props.onClick) {
        this.props.onClick();
      }
      else {
        const { data, projectIRI, useIfWorkflow, deeplink, source, sourceType, loadComponents } = this.props;

        // To avoid a component having to provide the list of possible components that we'll look at for linking
        // it can ask this component to load them. That keeps this more encapsulated.
        let components = this.props.components;
        if (!components && loadComponents) {
          components = await this.getProjectComponents();
        }
        const dataTypes = data.type ?
          Array.isArray(data.type) ?
            data.type
            :
            [data.type]
          :
          [];

        // End result should be one value
        const modeTypes = dataTypes.filter(d => MODE_KEYS.includes(d));
        const mode = MODE[modeTypes[0]];

        if (!mode) return null;

        const inComponent = Array.isArray(mode.inComponent) ?
          mode.inComponent.find(ic => data[ic])
          :
          mode.inComponent;

        const isNotLoaded = data._notAvailable;
        // Works whether an array or not
        const isInComponent = data[inComponent] && data[inComponent].length > 0 ? true : false;
        // Sometimes might be list of component IRIs and sometimes data objects
        const isProjectLink = components && isInComponent && components.some(co => co.id ? data[inComponent].includes(co.id) : data[inComponent].includes(co));

        let link = mode.applicationPath;
        if (isProjectLink) {
          let componentToUse = Array.isArray(data[inComponent]) ?
            data[inComponent][0]
            :
            data[inComponent];
          // If there is more than one scheme we try to find one with workflow activated is we are told to use workflow
          if (useIfWorkflow && components && components.length > 0 && Array.isArray(data[inComponent])) {
            const componentWithWorkflow = data[inComponent].find(s => components.find(c => c.id === s && useIfWorkflow));
            if (componentWithWorkflow) componentToUse = componentWithWorkflow;
          }
          link += '/project/' + mode.componentName + '#project=' + projectIRI + "&" + mode.componentName + "=" + componentToUse;
          if (mode.view) {
            link += "&view=" + mode.view;
          }
          link += "&" + mode.resourceName + "=" + data.id;
        }
        else if (!isNotLoaded && !isInComponent && mode.isComponent) {
          let componentLink = "/project/" + mode.componentName + "#project=" + projectIRI + "&" + mode.componentName + "=" + data.id;
          link += componentLink;
          // Are we deeplinking?
          if (deeplink && source && sourceType) {
            const sourceDataTypes = Array.isArray(sourceType) ?
              sourceType
              :
              [sourceType];

            // End result should be one value
            const sourceModeTypes = sourceDataTypes.filter(d => MODE_KEYS.includes(d));
            const sourceMode = MODE[sourceModeTypes[0]];
            if (sourceMode.view) {
              link += "&view=" + sourceMode.view;
            }
            link += "&" + sourceMode.resourceName + "=" + source;
          }
        }
        else if (!isNotLoaded) {
          link += '/project/floating#project=' + projectIRI;
          if (mode.view) {
            link += "&view=" + mode.view;
          }
          link += "&" + mode.resourceName + "=" + data.id;
        }

        if (link && (ctrlKey || this.props.forceNewTab)) {
          getNewTabLink(link);
          return;
        }
        if (this.props.onLink) this.props.onLink();
        this.props.history.push(link);
      }
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  render() {

    const { data, id, defaultLang, viewLang, compact, superCompact, wrap, highlight, isReadOnly, useIfWorkflow, showProject, dataTest,
      otherComponent, expectClass, useWorkflow, onDeleteIRI, propertyGraph, property, sourceGraph, contextProperty, context,
      reify, onReify, source, graph, applicableStatements, doNotdisplayNotLoaded, highlightNotAvailable, sourceType } = this.props;
    const { areYouSure, isErrored, disabled } = this.state;

    if (isErrored) return <Errored dataTest="ci-error" data={isErrored} />;

    const readOnly = isReadOnly || disabled || (!source && !onDeleteIRI);

    let output;
    const isNotLoaded = data._notAvailable || data.id.startsWith("http://www.w3.org/2002/07/owl#");

    // If isn't in DB then we simply render as an external link. No need to do all the other computation below
    if (isNotLoaded) {
      if (doNotdisplayNotLoaded) return null;
      output = <ExternalResourceLink
        dataTest={dataTest}
        id={id}
        highlight={highlight || highlightNotAvailable}
        annotated={reify}
        sourceGraph={sourceGraph}
        deleted={highlightNotAvailable}
        source={source}
        superCompact={superCompact}
        onDeleteIRI={onDeleteIRI}
        graph={graph}
        property={property}
        compact={compact}
        propertyGraph={propertyGraph}
        isReadOnly={readOnly} />;
      return wrap ?
        <div data-test="iri-display" style={{ maxWidth: '100%' }}>
          {output}
        </div>
        :
        reify ?
          <div style={{ whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%' }} data-test="iri-container">
            <ReifyButton
              subject={source}
              property={property}
              isReadOnly={readOnly}
              graph={graph}
              value={{
                "id": id
              }}
              applicableStatements={applicableStatements}
              onReify={onReify} />
            {output}
          </div>
          :
          output;
    }

    const dataTypes = data.type ?
      Array.isArray(data.type) ?
        data.type
        :
        [data.type]
      :
      [];

    // End result should be one value
    const modeTypes = dataTypes.filter(d => MODE_KEYS.includes(d));
    const mode = MODE[modeTypes[0]];

    if (!mode) return null;

    const inComponent = Array.isArray(mode.inComponent) ?
      mode.inComponent.find(ic => data[ic])
      :
      mode.inComponent;

    // Works whether an array or not
    const isInComponent = data[inComponent] && data[inComponent].length > 0 ? true : false;

    const isRejected = useWorkflow && !otherComponent && "status" in data && data.status[0] === REJECTED ? true : false;

    const doesNotHaveExpectedClass = mode.isComponent && expectClass && expectClass.length > 0 ?
      !dataTypes.includes(GS.getAlias(expectClass[0]))
      :
      expectClass && expectClass.length > 0 && dataTypes.length === 1 && !dataTypes.includes(GS.getAlias(expectClass[0]));

    const currentId = data.iri && data.iri.length > 0 ?
      data.iri[data.iri.length - 1]
      :
      data.id;

    const labelProp = mode.label;

    const isInContext = context && contextProperty && (data.visibility === context || data[contextProperty]) ?
      isInContextComponent(data, context)
      :
      false;

    const isOtherComponent = otherComponent || (contextProperty && !isInContext);

    if (mode.isComponent) {
      const av = data.editingOption && data.editingOption.includes(EDITING_OPTIONS.ADVANCED_VIEW);
      const sourceDataTypes = Array.isArray(sourceType) ?
        sourceType
        :
        [sourceType];
      const disabledComponent = (sourceDataTypes.includes("RdfsDatatype") || sourceDataTypes.includes("NamedIndividual")) && !av;
      output = <BasicComponentChip
        dataTest={dataTest}
        key={data.id}
        id={data.id}
        viewLang={viewLang}
        superCompact={superCompact}
        disabled={disabledComponent}
        invalid={doesNotHaveExpectedClass}
        useIfWorkflow={useIfWorkflow}
        compact={compact}
        defaultLang={defaultLang}
        graph={graph}
        isReadOnly={readOnly}
        onClick={this.handleClick}
        onDelete={!readOnly ? this.handleDeleteLink : null} />;
    }
    else {
      let linkAvatar = <LinkIcon fontSize="small" />;
      let projectData = {};
      const project = getProjectFromGraph(this.props.graph);
      try {
        if (GS.isLoaded(project, this.props.graph)) {
          projectData = GS.data(project, this.props.graph);
          if (projectData.type) {
            linkAvatar = computeProjectIcon(projectData.type, { color: '#ffffff', height: 20 })
          }
        }
      }
      catch (e) {
        return <Errored dataTest="cip-error" data={{ message: "Cannot load project information." }} />;
      }
      // Is this floating?
      const avatar = mode.allowFloating && !isInComponent ?
        <WavesIcon data-test="floating" fontSize="small" />
        :
        "additionalAvatar" in mode ?
          mode.additionalAvatar
          :
          isOtherComponent ?
            <NearMeIcon data-test="near-me" fontSize="small" />
            :
            sourceGraph && sourceGraph !== graph ?
              <span>{linkAvatar}</span>
              :
              null;
      // For XL labels there is only ever one language so we override any calculation on viewing language
      const displayLang = dataTypes.includes("Label") ?
        Object.keys(data.literalForm) ? Object.keys(data.literalForm)[0]
          :
          viewLang
        :
        viewLang;

      let projectTooltip = null;
      if ((showProject && graph) || (sourceGraph && sourceGraph !== graph)) {
        const projectTitle = projectData.title ?
          projectData.title[viewLang] ?
            projectData.title[viewLang]
            :
            projectData.title[defaultLang] || projectData.id
          :
          project;
        projectTooltip = <span>
          <span style={{ display: 'flex', marginTop: 4, marginBottom: 4, paddingBottom: 6, borderBottom: 'solid 1px #808080' }}>
            {linkAvatar} <span style={{ marginLeft: 6, paddingTop: 4 }} data-test="project-title">{projectTitle}</span>
          </span>
        </span>;
      }

      const tooltip = <span>
        {projectTooltip}
        <span data-test="tooltip-id" style={{ display: 'block', marginTop: 6, marginBottom: 6 }}>{currentId}</span>
        {((data.domain && data.domain.length > 0) || (data.range && data.range.length > 0)) &&
          <span style={{ display: 'block', borderTop: 'solid 1px #808080', paddingTop: 2, marginTop: 6, marginBottom: 6 }}>
            {data.domain && data.domain.length > 0 && <span style={{
              display: 'block',
              fontWeight: 400,
              marginTop: 4
            }}>
              <span style={{ display: 'inline-block', fontWeight: 500, width: 50, verticalAlign: 'top' }}>Domain</span>
              <span style={{ display: 'inline-block', width: 'calc(100% - 60px)' }}>{domainOrRange(data.domain)}</span>
            </span>}
            {data.range && data.range.length > 0 && <span style={{
              display: 'block',
              fontWeight: 400,
              marginTop: 4
            }}>
              <span style={{ display: 'inline-block', fontWeight: 500, width: 50, verticalAlign: 'top' }}>Range</span>
              <span style={{ display: 'inline-block', width: 'calc(100% - 60px)' }}>{domainOrRange(data.range)}</span>
            </span>}
          </span>
        }
      </span>;

      output = <BasicLangChip
        dataTest={dataTest}
        highlight={highlight}
        disabled={disabled || this.props.disabled}
        additionalIcon={isOtherComponent || avatar !== null}
        additionalAvatar={avatar}
        isReadOnly={readOnly}
        showProject={showProject}
        graph={graph}
        id={currentId}
        tooltip={tooltip}
        compact={compact}
        invalid={doesNotHaveExpectedClass}
        superCompact={superCompact}
        deprecated={data.deprecated === true}
        annotated={reify}
        deleted={data.pendingDeletion === true || isRejected}
        viewLang={displayLang}
        defaultLang={defaultLang}
        label={data[labelProp]}
        onClick={this.handleClick}
        onDelete={!readOnly ? this.handleDeleteLink : null} />;
    }

    return (
      <React.Fragment>
        {wrap ?
          <div data-test="iri-display" style={{ maxWidth: '100%' }}>
            {output}
          </div>
          :
          reify ?
            <div style={{ whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%' }} data-test="iri-container">
              <ReifyButton
                subject={source}
                property={property}
                isReadOnly={readOnly}
                graph={sourceGraph || graph}
                value={{
                  "id": id
                }}
                applicableStatements={applicableStatements}
                onReify={onReify} />
              <div style={{maxWidth: 'calc(100% - 32px)', display: 'inline-block'}}>
                {output}
              </div>
            </div>
            :
            output
        }
        {areYouSure && <AreYouSure onSure={this.onSure} onNotSure={this.onNotSure} message={this.props.checkIfUsedOnDelete.message} />}
      </React.Fragment>
    );

  }
}

CommonIriDisplay.defaultProps = {
  otherComponent: false,
  wrap: true,
  compact: false,
  deeplink: false,
  showProject: false,
  useIfWorkflow: undefined,
  loadComponents: false,
  forceNewTab: false,
  highlightNotAvailable: false,
  disabled: false,
  doNotdisplayNotLoaded: false
};

CommonIriDisplay.propTypes = {
  dataTest: PropTypes.string,
  isLoading: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  highlight: PropTypes.bool,
  highlightNotAvailable: PropTypes.bool,
  useIfWorkflow: PropTypes.bool,
  onDeleteIRI: PropTypes.func,
  source: PropTypes.string,
  otherComponent: PropTypes.bool,
  // Indicate if should link to a resource within a component
  deeplink: PropTypes.bool,
  expectClass: PropTypes.array,
  compact: PropTypes.bool,
  superCompact: PropTypes.bool,
  showProject: PropTypes.bool,
  useWorkflow: PropTypes.bool,
  property: PropTypes.string,
  forceNewTab: PropTypes.bool,
  doNotdisplayNotLoaded: PropTypes.bool,
  propertyGraph: PropTypes.string,
  onLink: PropTypes.func,
  onClick: PropTypes.func,
  context: PropTypes.string,
  contextProperty: PropTypes.string,
  graph: PropTypes.string.isRequired,
  wrap: PropTypes.bool,
  components: PropTypes.array,
  loadComponents: PropTypes.bool,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  sourceGraph: PropTypes.string,
  projectIRI: PropTypes.string.isRequired,
  reify: PropTypes.bool,
  onReify: PropTypes.func,
  applicableStatements: PropTypes.array,
  sourceType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ])
};

export default withStyles(styles)(withRouter(withGS(CommonIriDisplay, ["ALL"], null, {}, true))); 